'use client';
declare global {
  interface Window {
    _comscore: any[];
    COMSCORE?: { // COMSCORE might not be immediately available
      beacon: (config: object) => void;
    };
  }
}

const comscoreClientId = "28268321"; // Use environment variable

/**
 * Tracks a page view with Comscore.
 * This function should be called on initial page load and on subsequent client-side navigations.
 * It uses COMSCORE.beacon() for dynamic page updates as per Comscore documentation.
 *
 * @param {string} url - The full URL of the page being viewed.
 * @param {string} title - The title of the page being viewed.
 * @param {object} [additionalLabels] - Optional object for additional custom labels (e.g., 1P data).
 */
export const trackComscorePageView = (
  url: string,
  title: string,
  additionalLabels?: { [key: string]: string | number | any }
) => {
  // Ensure the COMSCORE object is available before attempting to use it.
  // The initial script in layout.tsx should load it.
  if (typeof window !== 'undefined' && window.COMSCORE && typeof window.COMSCORE.beacon === 'function') {
    // console.log(`Comscore: Tracking page view for URL: ${url} with title: ${title}`);

    // Parameters for COMSCORE.beacon() as per Comscore Tag Specification for dynamic pages.
    // c1=2 is fixed for web page impressions.
    const beaconConfig = {
      c1: "2",
      c2: comscoreClientId, // Use environment variable
      c7: url, // Full page URL
      c8: title, // Page Title
      // c9 (Referring URL) is automatically collected by the beacon.js script
      options: {
        enableFirstPartyCookie: true, // Re-affirm this option for each beacon call if desired
        bypassUserConsentRequirementFor1PCookie: true
      },
      ...additionalLabels, // Merge any additional custom labels (e.g., 1P data)
    };

    window.COMSCORE.beacon(beaconConfig);
    // Internal API call
		fetch('/api/comscore?ts=' + Date.now()).catch(e => console.log("Comscore internal API call failed:", e));
  } else {
    // console.warn('Comscore: COMSCORE.beacon() not available. Script might not have loaded yet or there was an error.');
    // Fallback: If COMSCORE.beacon is not ready, try pushing to _comscore array.
    // This might be less effective for dynamic page views if beacon() is the expected method.
    if (typeof window !== 'undefined' && window._comscore && typeof window._comscore.push === 'function') {
      window._comscore.push({
        c1: "2",
        c2: comscoreClientId,
        c7: url,
        c8: title,
        options: {
          enableFirstPartyCookie: true,
        },
        ...additionalLabels,
      });
      console.warn('Comscore: Falling back to _comscore.push for page view tracking.');
    }
  }
};

/**
 * Sends a generic event to Comscore.
 * This function provides a more abstract way to send various types of events
 * by pushing custom labels to the _comscore array.
 *
 * @param {string} eventName - A descriptive name for the event (e.g., 'button_click', 'form_submission').
 * @param {object} [eventProperties] - An object containing key-value pairs for event-specific labels.
 * These will be sent as custom labels to Comscore.
 * Example: { ns_event_category: 'Interaction', ns_event_action: 'Download', ns_event_label: 'Report PDF' }
 */
export const trackComscoreEvent = (
  eventName: string,
  eventProperties?: { [key: string]: string | number }
) => {
  if (typeof window !== 'undefined' && window._comscore && typeof window._comscore.push === 'function') {
    // console.log(`Comscore: Tracking event - ${eventName}`, eventProperties);

    // Comscore events are typically sent by pushing an object with custom labels.
    // The 'c1' and 'c2' parameters are usually required for any push.
    window._comscore.push({
      c1: "2", // Fixed value for web page impressions/events
      c2: comscoreClientId, // Your Comscore Publisher ID
      ns_event: eventName, // Custom label for the event name
      ...eventProperties, // Include any additional event-specific properties
    });
  } else {
    console.warn('Comscore: _comscore object not available for tracking event. Script might not have loaded yet.');
  }
};

/**
 * Tracks a click event with Comscore.
 * This is a specialized function that calls trackComscoreEvent with click-specific labels.
 *
 * @param {string} elementId - The ID of the clicked element (e.g., 'download-button').
 * @param {string} [category='Interaction'] - The category of the click (e.g., 'Navigation', 'Form').
 * @param {string} [action='Click'] - The action performed (e.g., 'Click', 'Submit').
 * @param {string} [label] - An optional label for more detail (e.g., 'Main CTA').
 */
export const trackComscoreClick = (
  elementId: string,
  category: string = 'Interaction',
  action: string = 'Click',
  label?: string,
  additionalParams?: { [key: string]: any }
) => {
  trackComscoreEvent('click', {
    ns_event_category: category,
    ns_event_action: action,
    ns_event_label: label || elementId, // Use elementId as label if no specific label provided
    ns_element_id: elementId, // Custom label for the element ID
    ...additionalParams // Include any additional parameters if needed
  });
};